// src/pages/ChecklistTemplateCreate.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import "../sass/pages/checklistTemplateCreate.scss";

/**
 * BACKEND (Plano A)
 *   POST /validacao/templates                              -> cria template (header)
 *   GET  /validacao/templates?include=versoes              -> lista templates + versões (apoio UX)
 *   GET  /validacao/templates/:id                          -> obtém header (legado pode trazer perguntas/versões)
 *   GET  /validacao/templates/:id/versoes                  -> lista versões do template
 *   POST /validacao/versoes/bootstrap/:templateId          -> clona ACTIVE -> DRAFT (reusa DRAFT)
 *   GET  /validacao/versoes/:versionId                     -> header + perguntas da versão
 *   POST /validacao/versoes/:versionId/perguntas?replace=1 -> salva perguntas na versão (DRAFT)
 *   PATCH/POST /validacao/versoes/:versionId/publicar      -> publica DRAFT -> ACTIVE
 */

// —— UI primitives ————————————————————————————————————————————————
const Button = ({ children, variant = "primary", className = "", ...props }) => (
  <button className={`ssw-btn ssw-btn--${variant} ${className}`} {...props}>
    {children}
  </button>
);

const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input ref={ref} className={`ssw-input ${className}`} {...props} />
));
Input.displayName = "Input";

const Textarea = React.forwardRef(({ className = "", ...props }, ref) => (
  <textarea ref={ref} className={`ssw-textarea ${className}`} {...props} />
));
Textarea.displayName = "Textarea";

const Switch = ({ checked, onChange, id }) => (
  <label className="ssw-switch">
    <input type="checkbox" id={id} checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="ssw-switch__slider" />
  </label>
);

// —— Helpers ————————————————————————————————————————————————
const SECTION_PREFIX = "__SSW_SECTION__::";

function cryptoRandom() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const newQ = (idx = 0) => ({
  tipo: "pergunta",
  _tmpId: cryptoRandom(),
  ordem: idx + 1,
  enunciado: "",
  obrigatoria: true,
});

const newSecao = (idx = 0) => ({
  tipo: "secao",
  _tmpId: cryptoRandom(),
  ordem: idx + 1,
  titulo: "",
  enunciado: "",
  obrigatoria: false,
});

function take(val, ...keys) {
  for (const k of keys) if (val && val[k] != null) return val[k];
  return undefined;
}

function isSectionText(value) {
  return String(value ?? "").startsWith(SECTION_PREFIX);
}

function sectionTitleFromText(value) {
  const text = String(value ?? "");
  return isSectionText(text) ? text.slice(SECTION_PREFIX.length).trim() : "";
}

function sectionText(title) {
  return `${SECTION_PREFIX}${String(title ?? "").trim()}`;
}

function adaptPerguntasFromApi(list = []) {
  return (Array.isArray(list) ? list : [])
    .map((p, i) => {
      const ordemRaw = take(p, "Ordem", "ordem", "ORDEM", "sequencia", "SEQUENCIA");
      const enunRaw = take(
        p,
        "Enunciado",
        "enunciado",
        "ENUNCIADO",
        "pergunta",
        "PERGUNTA",
        "descricao",
        "DESCRICAO",
      );
      const obgRaw = take(
        p,
        "Obrigatoria",
        "obrigatoria",
        "OBRIGATORIA",
        "obrigatorio",
        "OBRIGATORIO",
      );
      const enunciado = String(enunRaw ?? "");
      const secao = isSectionText(enunciado);

      return {
        id: take(p, "Id", "id", "ID") ?? null,
        tipo: secao ? "secao" : "pergunta",
        ordem: Number.isFinite(+ordemRaw) && +ordemRaw > 0 ? +ordemRaw : i + 1,
        titulo: secao ? sectionTitleFromText(enunciado) : "",
        enunciado: secao ? "" : enunciado,
        obrigatoria: secao ? false : !!obgRaw,
        _tmpId: p._tmpId || cryptoRandom(),
      };
    })
    .sort((a, b) => a.ordem - b.ordem)
    .map((p, i) => ({ ...p, ordem: i + 1 }));
}

function normalizePerguntasEdit(arr) {
  return (arr || [])
    .map((p, i) => {
      const tipo = p.tipo === "secao" ? "secao" : "pergunta";
      return {
        id: p.id ?? null,
        tipo,
        _tmpId: p._tmpId || cryptoRandom(),
        ordem: Number.isFinite(+p.ordem) && +p.ordem > 0 ? +p.ordem : i + 1,
        titulo: tipo === "secao" ? String(p.titulo ?? "") : "",
        enunciado: tipo === "pergunta" ? String(p.enunciado ?? "") : "",
        obrigatoria:
          tipo === "secao"
            ? false
            : p.obrigatoria === undefined
              ? true
              : !!p.obrigatoria,
      };
    })
    .sort((a, b) => a.ordem - b.ordem)
    .map((p, i) => ({ ...p, ordem: i + 1 }));
}

function normalizePerguntasPersist(arr) {
  return normalizePerguntasEdit(arr)
    .filter((p) =>
      p.tipo === "secao"
        ? String(p.titulo || "").trim().length > 0
        : String(p.enunciado || "").trim().length > 0,
    )
    .map((p, i) => ({
      ...p,
      ordem: i + 1,
      titulo: p.tipo === "secao" ? String(p.titulo || "").trim() : "",
      enunciado: p.tipo === "pergunta" ? String(p.enunciado || "").trim() : "",
      obrigatoria: p.tipo === "secao" ? false : !!p.obrigatoria,
    }));
}

function numeroPerguntaNaPosicao(lista, index) {
  return (lista || [])
    .slice(0, index + 1)
    .filter((item) => item.tipo !== "secao").length;
}

// —— Página ————————————————————————————————————————————————
export default function ChecklistTemplateCreate() {
  // Abas
  const [tab, setTab] = useState("criar"); // "criar" | "versoes"

  // Header (form)
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [termo, setTermo] = useState("");

  // Estado do Template/Versão
  const [tplId, setTplId] = useState(null);

  // Versionamento (lista e seleção)
  const [versoes, setVersoes] = useState([]); // [{id,numero,status,updatedAt}]
  const [versaoId, setVersaoId] = useState(null);
  const [versaoNumero, setVersaoNumero] = useState(null);
  const [versaoStatus, setVersaoStatus] = useState(null); // DRAFT | ACTIVE | ARCHIVED

  // Perguntas
  const [perguntas, setPerguntas] = useState([newQ(0)]);

  // Loading flags
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Lista de templates (apoio para iniciar edição/versão)
  const [templates, setTemplates] = useState([]);
  const [qTpl, setQTpl] = useState("");

  const tituloRef = useRef(null);

  const totalObrig = useMemo(
    () =>
      perguntas.filter(
        (p) => p.tipo !== "secao" && p.obrigatoria && p.enunciado.trim(),
      ).length,
    [perguntas],
  );
  const totalPerg = useMemo(
    () =>
      perguntas.filter(
        (p) => p.tipo !== "secao" && p.enunciado.trim(),
      ).length,
    [perguntas],
  );
  const totalSecoes = useMemo(
    () => perguntas.filter((p) => p.tipo === "secao" && p.titulo.trim()).length,
    [perguntas],
  );

  useEffect(() => {
    if (tab === "versoes") fetchTemplates(); // <— aqui
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ===== API (Plano A) — endpoints =====
  const endpoints = {
    /**
     * Lista TODOS os templates com suas versões.
     * A coluna "versao" da tabela usa a versão ACTIVE, se existir,
     * senão usa a última versão cadastrada, senão cai na Versao legada.
     */
    async listDraftTemplates() {
      const resAll = await api.get("/validacao/templates", {
        params: { include: "versoes" },
      });

      const arr = Array.isArray(resAll.data?.data)
        ? resAll.data.data
        : Array.isArray(resAll.data)
        ? resAll.data
        : [];

      return arr.map((x) => {
        const rawVersoes = Array.isArray(x.versoes ?? x.Versoes)
          ? x.versoes ?? x.Versoes
          : [];

        const versoes = rawVersoes
          .map((v) => ({
            numero:
              Number(
                v.numero ?? v.NumeroVersao ?? v.versao ?? v.Versao
              ) || 1,
            status: String(
              v.status ?? v.Status ?? "DRAFT"
            ).toUpperCase(),
            updatedAt:
              v.updatedAt ?? v.UpdatedAt ?? v.atualizadoEm ?? null,
          }))
          .sort((a, b) => b.numero - a.numero);

        const active = versoes.find((v) => v.status === "ACTIVE") || null;
        const latest = versoes[0] || null;
        const base = active || latest || null;

        return {
          id: Number(x.Id ?? x.id ?? x.ID) || null,
          titulo: String(x.Titulo ?? x.titulo ?? "").trim(),
          versao:
            base?.numero ||
            (Number(x.Versao ?? x.versao) || 1),
          atualizadoEm:
            base?.updatedAt ||
            x.UpdatedAt ||
            x.atualizadoEm ||
            x.updatedAt ||
            null,
        };
      });
    },

    async getTemplate(id) {
      const r = await api.get(`/validacao/templates/${id}`);
      return r.data;
    },

    async listVersions(templateId) {
      try {
        const r = await api.get(`/validacao/templates/${templateId}/versoes`);
        const arr = Array.isArray(r.data?.data)
          ? r.data.data
          : Array.isArray(r.data)
          ? r.data
          : [];
        return arr
          .map((v) => ({
            id: Number(
              v.id ?? v.Id ?? v.ID ?? v.version_id
            ) || null,
            numero:
              Number(
                v.numero ??
                  v.NumeroVersao ??
                  v.versao ??
                  v.Versao
              ) || null,
            status: String(
              v.status ?? v.Status ?? "DRAFT"
            ).toUpperCase(),
            updatedAt:
              v.updatedAt ?? v.UpdatedAt ?? v.atualizadoEm ?? null,
          }))
          .sort((a, b) => b.numero - a.numero);
      } catch {
        // fallback: algumas APIs podem retornar versões dentro do GET template
        const meta = await endpoints.getTemplate(templateId);
        const raw = Array.isArray(meta?.versoes ?? meta?.Versions)
          ? meta.versoes ?? meta.Versions
          : [];
        return raw
          .map((v) => ({
            id: Number(
              v.id ?? v.Id ?? v.ID ?? v.version_id
            ) || null,
            numero:
              Number(
                v.numero ??
                  v.NumeroVersao ??
                  v.versao ??
                  v.Versao
              ) || null,
            status: String(
              v.status ?? v.Status ?? "DRAFT"
            ).toUpperCase(),
            updatedAt:
              v.updatedAt ?? v.UpdatedAt ?? v.atualizadoEm ?? null,
          }))
          .sort((a, b) => b.numero - a.numero);
      }
    },

    async createTemplate(meta) {
      const r = await api.post("/validacao/templates", meta);
      return r.data; // { id, ... }
    },

    async bootstrapDraftVersion(templateId) {
      const r = await api.post(`/validacao/templates/${templateId}/versoes`);
      return r.data; // { id|version_id, numero|NumeroVersao, status, reuse? }
    },

    async getVersion(versionId) {
      const r = await api.get(`/validacao/versoes/${versionId}`);
      return r.data;
    },

    async patchVersionHeader(
      versionId,
      { titulo, descricao, termo_responsabilidade }
    ) {
      const r = await api.patch(`/validacao/versoes/${versionId}`, {
        titulo,
        descricao,
        termo_responsabilidade,
      });
      return r.data;
    },

    async savePerguntasOnVersion(versionId, perguntas, { replace }) {
      const payload = {
        perguntas: perguntas.map((p, i) => ({
          ordem: i + 1,
          enunciado:
            p.tipo === "secao" ? sectionText(p.titulo) : p.enunciado,
          obrigatoria: p.tipo === "secao" ? false : !!p.obrigatoria,
        })),
      };
      await api.post(
        `/validacao/versoes/${versionId}/perguntas`,
        payload,
        {
          params: replace ? { replace: "1" } : undefined,
        }
      );
    },

    async reorderPerguntas(
      versionId,
      ordemArray /* [{pergunta_id, ordem}] */
    ) {
      await api.patch(
        `/validacao/versoes/${versionId}/perguntas/reordenar`,
        { ordem: ordemArray }
      );
    },

    async publishVersion(versionId) {
      await api.post(`/validacao/versoes/${versionId}/publicar`);
    },
  };

  // ===== Lista de templates para abrir/versionar =====
  async function fetchTemplates() {
    setLoadingTemplates(true);
    try {
      const all = await endpoints.listDraftTemplates();
      const normalized = all.filter((x) =>
        qTpl
          ? x.titulo.toLowerCase().includes(qTpl.toLowerCase())
          : true
      );
      setTemplates(normalized);
    } catch (e) {
      console.error(e);
      showError(
        e?.response?.data?.error ||
          "Falha ao carregar templates."
      );
    } finally {
      setLoadingTemplates(false);
    }
  }

  // ===== Carregar Template e suas versões =====
  async function selectAndLoadLatestVersion(templateId) {
    const lista = await endpoints.listVersions(templateId);
    setVersoes(lista);
    const latest = lista[0];
    if (latest?.id) {
      setVersaoId(latest.id);
      setVersaoNumero(latest.numero ?? null);
      setVersaoStatus(latest.status ?? null);
      const metaV = await endpoints.getVersion(latest.id);
      const pergs = Array.isArray(metaV?.perguntas)
        ? adaptPerguntasFromApi(metaV.perguntas)
        : [];
      setPerguntas(
        pergs.length ? normalizePerguntasEdit(pergs) : [newQ(0)]
      );
    } else {
      // sem versões ainda
      setVersaoId(null);
      setVersaoNumero(null);
      setVersaoStatus("DRAFT");
      setPerguntas([newQ(0)]);
    }
  }

  async function loadTemplateIntoForm(id) {
    if (!id) return;
    setLoading(true);
    try {
      const meta = await endpoints.getTemplate(id);

      setTplId(Number(id));
      setTitulo(
        String(meta.Titulo ?? meta.titulo ?? "").trim()
      );
      setDescricao(
        String(meta.Descricao ?? meta.descricao ?? "").trim()
      );
      setTermo(
        String(
          meta.TermoResponsabilidade ??
            meta.termo_responsabilidade ??
            ""
        ).trim()
      );

      // carrega painel de versões e a mais recente (com perguntas)
      await selectAndLoadLatestVersion(id);

      setTab("criar");
      showInfo("Template carregado para versionamento.");
    } catch (e) {
      console.error(e);
      showError(
        e?.response?.data?.error ||
          "Falha ao abrir template."
      );
    } finally {
      setLoading(false);
    }
  }

  // Trocar versão selecionada
  async function handleSelectVersao(v) {
    if (!v?.id) return;
    setLoading(true);
    try {
      const meta = await endpoints.getVersion(v.id);
      const pergs = Array.isArray(meta.perguntas)
        ? adaptPerguntasFromApi(meta.perguntas)
        : [];
      setPerguntas(
        pergs.length ? normalizePerguntasEdit(pergs) : [newQ(0)]
      );
      setVersaoId(v.id);
      setVersaoNumero(v.numero || null);
      setVersaoStatus(v.status || null);
    } catch (e) {
      console.error(e);
      showError("Falha ao trocar a versão.");
    } finally {
      setLoading(false);
    }
  }

  // Persistência de perguntas por versão
  async function savePerguntas(versionIdParam, base) {
    const vid = Number(versionIdParam ?? versaoId);
    if (!vid)
      throw new Error(
        "Versão inexistente. Crie a DRAFT antes de salvar."
      );
    await endpoints.savePerguntasOnVersion(vid, base, {
      replace: true,
    });
  }

  // — Ações ————————————————————————————————————————————————
  async function ensureDraftVersion(templateId) {
    const currentStatus = String(versaoStatus || "").toUpperCase();

    // Se já estamos em uma DRAFT, ela pode ser editada diretamente.
    if (versaoId && currentStatus === "DRAFT") {
      return {
        id: versaoId,
        numero: versaoNumero,
        status: "DRAFT",
      };
    }

    // Antes de criar outra versão, reaproveita uma DRAFT existente.
    try {
      const lista = await endpoints.listVersions(templateId);
      const draftExistente = lista.find(
        (v) => String(v.status || "").toUpperCase() === "DRAFT" && v.id,
      );

      if (draftExistente) {
        setVersoes(lista);
        setVersaoId(draftExistente.id);
        setVersaoNumero(draftExistente.numero ?? null);
        setVersaoStatus("DRAFT");
        return draftExistente;
      }
    } catch (e) {
      console.warn("[Checklist] Falha ao procurar DRAFT existente:", e);
    }

    try {
      const data = await endpoints.bootstrapDraftVersion(templateId);
      const v = {
        id:
          Number(data?.id ?? data?.Id ?? data?.ID ?? data?.version_id) || null,
        numero:
          Number(
            data?.numero ??
              data?.NumeroVersao ??
              data?.versao ??
              data?.Versao,
          ) || null,
        status: String(data?.status ?? data?.Status ?? "DRAFT").toUpperCase(),
        updatedAt: data?.updatedAt ?? data?.UpdatedAt ?? null,
      };

      if (!v.id) {
        throw new Error("A API não retornou o ID da versão DRAFT.");
      }

      setVersoes((prev) => {
        const semDuplicar = prev.filter((item) => item.id !== v.id);
        return [v, ...semDuplicar].sort((a, b) => (b.numero || 0) - (a.numero || 0));
      });
      setVersaoId(v.id);
      setVersaoNumero(v.numero);
      setVersaoStatus("DRAFT");
      return { ...v, status: "DRAFT" };
    } catch (e) {
      // Alguns backends respondem 409 quando já existe uma única DRAFT.
      // Nesse caso, recarrega e reaproveita a DRAFT em vez de falhar a tela.
      if (e?.response?.status === 409) {
        const lista = await endpoints.listVersions(templateId);
        const draftExistente = lista.find(
          (v) => String(v.status || "").toUpperCase() === "DRAFT" && v.id,
        );

        if (draftExistente) {
          setVersoes(lista);
          setVersaoId(draftExistente.id);
          setVersaoNumero(draftExistente.numero ?? null);
          setVersaoStatus("DRAFT");
          return draftExistente;
        }
      }

      throw e;
    }
  }

  async function salvarCabecalhoDraft(versionId) {
    await endpoints.patchVersionHeader(versionId, {
      titulo: titulo.trim(),
      descricao: descricao?.trim() || null,
      termo_responsabilidade: termo?.trim() || null,
    });
  }

  async function handleSalvarVersao() {
    if (!titulo.trim()) {
      showError("Informe o título do checklist.");
      tituloRef.current?.focus();
      return;
    }

    const base = normalizePerguntasPersist(perguntas);
    const totalPerguntasValidas = base.filter((p) => p.tipo !== "secao").length;

    if (totalPerguntasValidas === 0) {
      showError("Inclua ao menos uma pergunta.");
      return;
    }

    setLoading(true);
    try {
      let templateId = tplId;

      if (!templateId) {
        const res = await endpoints.createTemplate({
          titulo: titulo.trim(),
          descricao: descricao?.trim() || null,
          termo_responsabilidade: termo?.trim() || null,
        });

        templateId = res?.id ?? res?.ID ?? res?.Id;
        if (!templateId) {
          throw new Error("Falha ao criar template (ID ausente).");
        }
        setTplId(Number(templateId));
      }

      // ACTIVE nunca é alterada diretamente. Ao salvar, cria/reutiliza DRAFT.
      const v = await ensureDraftVersion(templateId);
      if (!v?.id) throw new Error("Falha ao obter versão DRAFT.");

      await salvarCabecalhoDraft(v.id);
      await savePerguntas(v.id, base);

      setVersaoId(v.id);
      setVersaoNumero(v.numero ?? null);
      setVersaoStatus("DRAFT");

      showInfo(`Versão v${v.numero ?? "-"} salva como DRAFT.`);
    } catch (e) {
      console.error(e);
      showError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Falha ao salvar versão.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handlePublicar() {
    if (!tplId) return showError("Crie o template antes de publicar.");

    const base = normalizePerguntasPersist(perguntas);
    const totalPerguntasValidas = base.filter((p) => p.tipo !== "secao").length;
    if (totalPerguntasValidas === 0) {
      return showError("Inclua ao menos uma pergunta para publicar.");
    }

    setPublishing(true);
    try {
      // Sempre publica uma DRAFT. Se a tela estiver em ACTIVE, gera/reutiliza DRAFT.
      const v = await ensureDraftVersion(tplId);
      if (!v?.id) throw new Error("Falha ao preparar versão DRAFT para publicação.");

      await salvarCabecalhoDraft(v.id);
      await savePerguntas(v.id, base);
      await endpoints.publishVersion(v.id);

      try {
        await selectAndLoadLatestVersion(tplId);
      } catch (refreshError) {
        console.warn("[Checklist] Publicou, mas não conseguiu recarregar versões:", refreshError);
        setVersaoStatus("ACTIVE");
      }

      showInfo("Versão publicada com sucesso.");
    } catch (e) {
      console.error(e);
      showError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Falha ao publicar.",
      );
    } finally {
      setPublishing(false);
    }
  }

  async function handleCriarNovaVersao() {
    if (!tplId) return showError("Crie o template antes de versionar.");

    setLoading(true);
    try {
      const lista = await endpoints.listVersions(tplId);
      const draftExistente = lista.find(
        (v) => String(v.status || "").toUpperCase() === "DRAFT" && v.id,
      );

      if (draftExistente) {
        await handleSelectVersao(draftExistente);
        showInfo(`Já existe a DRAFT v${draftExistente.numero ?? "-"}. Ela foi aberta para edição.`);
        return;
      }

      await endpoints.bootstrapDraftVersion(tplId);
      await selectAndLoadLatestVersion(tplId);
      showInfo("Nova versão DRAFT criada.");
    } catch (e) {
      if (e?.response?.status === 409) {
        try {
          const lista = await endpoints.listVersions(tplId);
          const draftExistente = lista.find(
            (v) => String(v.status || "").toUpperCase() === "DRAFT" && v.id,
          );
          if (draftExistente) {
            await handleSelectVersao(draftExistente);
            showInfo(`A DRAFT v${draftExistente.numero ?? "-"} já existia e foi aberta.`);
            return;
          }
        } catch (reloadError) {
          console.error(reloadError);
        }
      }

      console.error(e);
      showError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Falha ao criar DRAFT.",
      );
    } finally {
      setLoading(false);
    }
  }

  function resetarFormulario() {
    setTitulo("");
    setDescricao("");
    setTermo("");
    setPerguntas([newQ(0)]);
    setTplId(null);
    setVersoes([]);
    setVersaoId(null);
    setVersaoNumero(null);
    setVersaoStatus(null);
    tituloRef.current?.focus();
  }

  const canPublish =
    Boolean(titulo.trim()) &&
    totalPerg > 0 &&
    !!tplId &&
    versaoStatus !== "ACTIVE" &&
    !publishing;

  // Perguntas (form)
  function addPergunta() {
    setPerguntas((prev) =>
      normalizePerguntasEdit([...prev, newQ(prev.length)]),
    );
  }

  function addSecao() {
    setPerguntas((prev) =>
      normalizePerguntasEdit([...prev, newSecao(prev.length)]),
    );
  }
  function removePergunta(tmpId) {
    setPerguntas((prev) =>
      normalizePerguntasEdit(
        prev.filter((p) => p._tmpId !== tmpId)
      )
    );
  }
  function updatePergunta(tmpId, patch) {
    setPerguntas((prev) =>
      normalizePerguntasEdit(
        prev.map((p) =>
          p._tmpId === tmpId ? { ...p, ...patch } : p
        )
      )
    );
  }
  function mover(tmpId, dir) {
    const ordered = normalizePerguntasEdit(perguntas);
    const idx = ordered.findIndex((x) => x._tmpId === tmpId);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= ordered.length) return;
    const clone = [...ordered];
    const [item] = clone.splice(idx, 1);
    clone.splice(j, 0, item);
    setPerguntas(
      clone.map((p, i) => ({ ...p, ordem: i + 1 }))
    );
  }

  // Render
  return (
    <MainLayout>
      <div className="ssw-container ssw-page-checklist ssw-container--xl">
        {/* Abas */}
        <div className="ssw-tabs">
          <button
            className={`ssw-tab ${
              tab === "criar" ? "is-active" : ""
            }`}
            onClick={() => setTab("criar")}
          >
            Criar / Editar
          </button>
          <button
            className={`ssw-tab ${
              tab === "versoes" ? "is-active" : ""
            }`}
            onClick={() => setTab("versoes")}
          >
            Versões (todos os templates)
          </button>
        </div>

        {/* ====== Aba: Criar / Editar ====== */}
        {tab === "criar" && (
          <>
            {/* Header */}
            <div className="ssw-header">
              <div>
                <h1>
                  {tplId
                    ? "Editar checklist"
                    : "Novo checklist"}
                </h1>
                <div className="ssw-sub">
                  {tplId ? (
                    <>
                      <span
                        className={`ssw-badge ${
                          versaoStatus === "ACTIVE"
                            ? "is-green"
                            : "is-gray"
                        }`}
                      >
                        {versaoStatus || "DRAFT"}
                      </span>
                      <span className="ssw-kpi">
                        Template: {tplId}
                      </span>
                      <span className="ssw-kpi">
                        Versão: {versaoNumero ?? "-"}
                      </span>
                      <span className="ssw-kpi">
                        Perguntas: {totalPerg} (
                        {totalObrig} obrig.)
                      </span>
                    </>
                  ) : (
                    <span className="ssw-badge is-blue">
                      Novo
                    </span>
                  )}
                </div>
              </div>
              <div className="ssw-actions">
                {tplId && (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        selectAndLoadLatestVersion(tplId)
                      }
                      disabled={loading || publishing}
                    >
                      Atualizar versões
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleCriarNovaVersao}
                      disabled={loading || publishing}
                    >
                      + Nova versão (DRAFT)
                    </Button>
                  </>
                )}
                <Button
                  onClick={handleSalvarVersao}
                  disabled={loading || publishing}
                >
                  {loading ? "Gravando…" : "Salvar versão"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handlePublicar}
                  disabled={!canPublish || loading}
                >
                  {publishing
                    ? "Publicando…"
                    : "Publicar versão"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={resetarFormulario}
                  disabled={loading || publishing}
                >
                  Resetar
                </Button>
              </div>
            </div>

            {/* Painel lateral de versões */}
            {tplId && versoes.length > 0 && (
              <div className="ssw-versions">
                <div className="ssw-card">
                  <div className="ssw-card__head">
                    <h3>Versões</h3>
                  </div>
                  <ul className="ssw-version-list">
                    {versoes.map((v) => {
                      const active = v.id === versaoId;
                      return (
                        <li
                          key={v.id}
                          className={`ssw-version-item ${
                            active ? "is-active" : ""
                          }`}
                        >
                          <button
                            className="ssw-version-btn"
                            onClick={() => handleSelectVersao(v)}
                          >
                            <span className="ssw-version-num">
                              v{v.numero}
                            </span>
                            <span
                              className={`ssw-chip ${
                                v.status === "ACTIVE"
                                  ? "is-green"
                                  : v.status === "DRAFT"
                                  ? "is-blue"
                                  : "is-gray"
                              }`}
                            >
                              {v.status}
                            </span>
                            {v.updatedAt && (
                              <span className="ssw-version-date">
                                {new Date(
                                  v.updatedAt
                                ).toLocaleString()}
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}

            {/* Metadados */}
            <div className="ssw-card">
              <div className="ssw-grid ssw-grid--2">
                <div className="ssw-field">
                  <label>Título *</label>
                  <Input
                    ref={tituloRef}
                    value={titulo}
                    maxLength={200}
                    onChange={(e) =>
                      setTitulo(e.target.value)
                    }
                    placeholder="Ex.: Checklist de ativação fiscal"
                  />
                </div>
                <div className="ssw-field">
                  <label>Descrição</label>
                  <Input
                    value={descricao}
                    maxLength={1000}
                    onChange={(e) =>
                      setDescricao(e.target.value)
                    }
                    placeholder="Breve descrição para contexto"
                  />
                </div>
              </div>

              <div className="ssw-field">
                <label>
                  Termo de responsabilidade (apresentado na
                  assinatura)
                </label>
                <Textarea
                  value={termo}
                  rows={6}
                  onChange={(e) =>
                    setTermo(e.target.value)
                  }
                  placeholder="Ex.: Declaro estar ciente de que as respostas representam..."
                />
              </div>
            </div>

            {/* Perguntas e títulos de seção */}
            <div className="ssw-card">
              <div className="ssw-card__head">
                <div>
                  <h2>Perguntas</h2>
                  <div className="ssw-hint" style={{ marginTop: 4 }}>
                    Use títulos para separar blocos como Instalação, Configuração e Fiscal.
                  </div>
                </div>
                <div className="ssw-actions">
                  <Button
                    variant="ghost"
                    onClick={addSecao}
                    disabled={publishing}
                  >
                    + Adicionar título
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={addPergunta}
                    disabled={publishing}
                  >
                    + Adicionar pergunta
                  </Button>
                </div>
              </div>

              <div className="ssw-tablewrap">
                <table className="ssw-table ssw-table--compact">
                  <thead>
                    <tr>
                      <th style={{ width: 72 }}>Ordem</th>
                      <th>Enunciado *</th>
                      <th style={{ width: 140 }}>Obrigatória</th>
                      <th style={{ width: 160 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {perguntas.length === 0 && (
                      <tr>
                        <td colSpan={4} className="ssw-empty">
                          Nenhuma pergunta
                        </td>
                      </tr>
                    )}

                    {perguntas.map((p, idx) => {
                      if (p.tipo === "secao") {
                        return (
                          <tr key={p._tmpId} className="ssw-section-row">
                            <td colSpan={4}>
                              <div className="ssw-section-editor">
                                <div className="ssw-section-editor__label">Título</div>
                                <Input
                                  value={p.titulo || ""}
                                  maxLength={180}
                                  placeholder="Ex.: Instalação, Configuração, Fiscal..."
                                  onChange={(e) =>
                                    updatePergunta(p._tmpId, {
                                      titulo: e.target.value,
                                    })
                                  }
                                  disabled={publishing}
                                />
                                <div className="ssw-row-actions">
                                  <Button
                                    variant="ghost"
                                    onClick={() => mover(p._tmpId, -1)}
                                    disabled={idx === 0 || publishing}
                                    title="Mover título para cima"
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    onClick={() => mover(p._tmpId, +1)}
                                    disabled={idx === perguntas.length - 1 || publishing}
                                    title="Mover título para baixo"
                                  >
                                    ↓
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    onClick={() => removePergunta(p._tmpId)}
                                    disabled={publishing}
                                  >
                                    Remover
                                  </Button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      const numeroPergunta = numeroPerguntaNaPosicao(perguntas, idx);

                      return (
                        <tr key={p._tmpId}>
                          <td>
                            <Input
                              type="number"
                              value={numeroPergunta}
                              readOnly
                              disabled
                              title="A numeração das perguntas é automática"
                            />
                          </td>
                          <td>
                            <Input
                              value={p.enunciado}
                              maxLength={500}
                              placeholder={`Ex.: Verificar ${numeroPergunta} …`}
                              onChange={(e) =>
                                updatePergunta(p._tmpId, {
                                  enunciado: e.target.value,
                                })
                              }
                              disabled={publishing}
                            />
                          </td>
                          <td className="text-center">
                            <Switch
                              id={`obg-${p._tmpId}`}
                              checked={p.obrigatoria}
                              onChange={(v) =>
                                updatePergunta(p._tmpId, {
                                  obrigatoria: v,
                                })
                              }
                            />
                          </td>
                          <td className="ssw-row-actions">
                            <Button
                              variant="ghost"
                              onClick={() => mover(p._tmpId, -1)}
                              disabled={idx === 0 || publishing}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => mover(p._tmpId, +1)}
                              disabled={idx === perguntas.length - 1 || publishing}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => removePergunta(p._tmpId)}
                              disabled={publishing}
                            >
                              Remover
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="ssw-hint">
                A numeração das perguntas é automática. Títulos não contam como pergunta e
                não exigem resposta. Campos vazios são descartados ao salvar.
              </div>
            </div>

            {/* Footer actions */}
            <div className="ssw-stickybar">
              <div className="ssw-stickybar__left">
                <span className="ssw-badge">
                  {totalPerg} perguntas
                </span>
                <span className="ssw-badge">
                  {totalObrig} obrigatórias
                </span>
                {totalSecoes > 0 && (
                  <span className="ssw-badge">
                    {totalSecoes} títulos
                  </span>
                )}
                {tplId && (
                  <span className="ssw-kpi">
                    Template #{tplId}
                  </span>
                )}
                {versaoNumero != null && (
                  <span className="ssw-kpi">
                    v{versaoNumero}
                  </span>
                )}
              </div>
              <div className="ssw-stickybar__right">
                {tplId && (
                  <Button
                    variant="ghost"
                    onClick={handleCriarNovaVersao}
                    disabled={loading || publishing}
                  >
                    + Nova versão
                  </Button>
                )}
                <Button
                  onClick={handleSalvarVersao}
                  disabled={loading || publishing}
                >
                  {loading ? "Gravando…" : "Salvar versão"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handlePublicar}
                  disabled={!canPublish || loading}
                >
                  {publishing
                    ? "Publicando…"
                    : "Publicar versão"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={resetarFormulario}
                  disabled={loading || publishing}
                >
                  Resetar
                </Button>
              </div>
            </div>
          </>
        )}

        {/* ====== Aba: Versões (lista de templates para abrir) ====== */}
        {tab === "versoes" && (
          <div className="ssw-card">
            <div className="ssw-card__head">
              <h2>Templates</h2>
              <div
                className="ssw-actions"
                style={{ gap: 8 }}
              >
                <Input
                  placeholder="Buscar por título…"
                  value={qTpl}
                  onChange={(e) => setQTpl(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter"
                      ? fetchTemplates()
                      : null
                  }
                  style={{ width: 280 }}
                />
                <Button
                  variant="ghost"
                  onClick={fetchTemplates}
                  disabled={loadingTemplates}
                >
                  {loadingTemplates
                    ? "Atualizando…"
                    : "Atualizar"}
                </Button>
              </div>
            </div>

            <div className="ssw-tablewrap">
              <table className="ssw-table">
                <thead>
                  <tr>
                    <th style={{ width: 110 }}>ID</th>
                    <th>Título</th>
                    <th style={{ width: 110 }}>Versão</th>
                    <th style={{ width: 220 }}>
                      Atualizado em
                    </th>
                    <th style={{ width: 200 }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTemplates ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="ssw-empty"
                      >
                        Carregando…
                      </td>
                    </tr>
                  ) : templates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="ssw-empty"
                      >
                        Nenhum template encontrado.
                      </td>
                    </tr>
                  ) : (
                    templates.map((d) => (
                      <tr key={d.id}>
                        <td>#{d.id}</td>
                        <td>{d.titulo || "-"}</td>
                        <td>{d.versao ?? "-"}</td>
                        <td>
                          {d.atualizadoEm
                            ? new Date(
                                d.atualizadoEm
                              ).toLocaleString()
                            : "-"}
                        </td>
                        <td className="ssw-row-actions">
                          <Button
                            variant="secondary"
                            onClick={() =>
                              loadTemplateIntoForm(d.id)
                            }
                          >
                            Abrir
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="ssw-hint">
              Abra qualquer template (ativo ou não) para
              editar e lançar novas versões. Ao salvar, as
              perguntas são persistidas em{" "}
              <code>/validacao/versoes/:versionId</code>. Para
              publicar, use “Publicar versão”.
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
